import React from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import Profile from '../../shared/components/Profile';
import { PROFILE_GROUPS } from '../../shared/components/Profile/constants';
import useMyProfileService from '../../services/MyProfileService/myProfile.service';
import { useAuth } from '../../context/AuthContext';
import { AllowedRoles } from '../../routes/routeConstants/allowedRoles';
import { AppRoutes } from '../../routes/routeConstants/appRoutes';
import { EditProfilePayload } from '../../models/profile.model';

const MyProfile: React.FC = () => {
  const navigate = useNavigate();
  const { getProfile, editProfile, setProfilePassword } = useMyProfileService();
  const { logout, canAccess } = useAuth();

  const { data } = useQuery(getProfile());
  const { mutate: saveProfile, isPending: isSaving } = useMutation(editProfile());
  const { mutate: savePassword, isPending: isSavingPassword } = useMutation(setProfilePassword());

  const profile = data?.profile;
  const canEditGroups = canAccess({ allowedRoles: AllowedRoles.ONLY_ADMINS });

  const handleSave = (payload: EditProfilePayload, onDone: () => void) => {
    saveProfile(payload, {
      onSuccess: (result) => {
        onDone();
        if (result.emailChanged) logout();
      },
    });
  };

  const handleSetPassword = (newPassword: string, onSuccess: () => void) => {
    savePassword(
      { password: newPassword, confirmPassword: newPassword },
      {
        onSuccess: (result) => {
          onSuccess();
          if (result.requiresLogout) logout();
        },
      },
    );
  };

  const handleEditGroupsClick = () => navigate(AppRoutes.GROUP_MANAGER);

  return (
    <Profile
      profile={profile}
      isSaving={isSaving}
      onSave={handleSave}
      passwordIsSubmitting={isSavingPassword}
      onSetPassword={handleSetPassword}
      groups={PROFILE_GROUPS}
      canEditGroups={canEditGroups}
      onEditGroupsClick={handleEditGroupsClick}
      showSubscription
      subscription={data?.subscription}
      renewalStatus={data?.renewalStatus}
      subscriptionStatus={data?.subscriptionStatus}
    />
  );
};

export default MyProfile;
